import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, User, LayoutGrid, Mail, Globe, Smartphone, GitBranch } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useTheme } from '../contexts/ThemeContext';
import { applyAlpha, humanizeToken } from '../lib/colorUtils';
import AccountMenu from './AccountMenu';
import SettingsModal from './SettingsModal';
import MarketResearchModal from './MarketResearchModal';
import MessageActions from './MessageActions';
import BranchIndicator from './BranchIndicator';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from './ui/select';

const SimpleChatView = ({
  conversation,
  onSendMessage,
  onSwitchView,
  brandProfileSpec,
  brandMeta,
  brandApplicationSlice,
  brandContextPacks,
  brandVisualTheme,
  canSendMessage = true,
  onRequireAuth,
  authStatus,
  sessionUser,
  accountLabel,
  onLogin,
  onLogout,
  brands,
  activeBrandId,
  onBrandChange,
  availableEnvironments,
  activeEnvironment,
  onEnvironmentChange,
  onUpdateUser,
  streamingMessage,
  onBranchMessage,
  onOpenBranchTree,
  branchIndex = 1,
  totalBranches = 1,
}) => {
  const { resolvedTheme } = useTheme();
  const themeMode = brandVisualTheme?.modes?.[resolvedTheme] ?? brandVisualTheme?.modes?.light;
  const isBrandTheme = resolvedTheme === 'noderiver';
  const heroGradient = themeMode?.heroGradient ?? brandVisualTheme?.gradients?.hero ?? 'linear-gradient(135deg, #1E8997, #0e273a)';
  const accentGradient = themeMode?.ctaGradient ?? brandVisualTheme?.gradients?.cta ?? 'linear-gradient(135deg, #1E8997, #0e273a)';
  const containerStyle = isBrandTheme
    ? { background: heroGradient }
    : { backgroundColor: themeMode?.background ?? '#ffffff' };
  const textColor = isBrandTheme
    ? brandVisualTheme?.modes?.noderiver?.text ?? '#ffffff'
    : themeMode?.text ?? '#0f172a';
  const mutedColor = isBrandTheme
    ? brandVisualTheme?.modes?.noderiver?.muted ?? 'rgba(255,255,255,0.8)'
    : themeMode?.muted ?? '#6b7280';
  const accentColor = themeMode?.accent ?? brandVisualTheme?.palette?.primary ?? '#1E8997';
  const accentContrast = themeMode?.accentContrast ?? brandVisualTheme?.palette?.textOnPrimary ?? '#ffffff';
  const inputBg = themeMode?.input ?? '#ffffff';
  const statusColors = {
    working: isBrandTheme ? 'rgba(255,255,255,0.95)' : '#fb923c',
    idle: isBrandTheme ? 'rgba(255,255,255,0.9)' : '#22c55e',
    ready: isBrandTheme ? 'rgba(255,255,255,0.85)' : '#facc15',
  };
  const quickActionBackground = isBrandTheme ? 'rgba(255,255,255,0.08)' : themeMode?.surface ?? '#ffffff';
  const quickActionBorder = isBrandTheme ? 'rgba(255,255,255,0.3)' : themeMode?.border ?? '#e5e7eb';
  const quickActionText = isBrandTheme ? textColor : themeMode?.text ?? '#111827';
  const quickActions = [
    { label: 'Draft Email', icon: Mail, value: 'Draft an email about our new product launch' },
    { label: 'Website Content', icon: Globe, value: 'Create a hero section for our homepage' },
    { label: 'Social Media', icon: Smartphone, value: 'Write a LinkedIn post about our sustainability goals' },
  ];
  const [message, setMessage] = useState('');
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [researchContent, setResearchContent] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  // Track tested content to reset modal state properly
  const [activeTestContent, setActiveTestContent] = useState(null);
  
  const scrollRef = useRef(null);
  const brandOptions = Array.isArray(brands) ? brands : [];
  const environmentOptions = Array.isArray(availableEnvironments) ? availableEnvironments : [];
  const selectedBrandId = activeBrandId ?? brandMeta?.id ?? (brandOptions[0]?.id ?? '');
  const selectedEnvironment = activeEnvironment ?? 'production';
  
  // Determine if we're streaming for this conversation
  const isStreaming = streamingMessage && streamingMessage.conversationId === conversation?.id;
  const streamingContent = isStreaming ? streamingMessage.content : '';

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation?.messages, isStreaming, streamingContent]);

  const handleSend = async () => {
    if (!message.trim() || isStreaming) return;
    const messageContent = message;
    setMessage('');
    await onSendMessage(messageContent, true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenResearch = (content) => {
    setResearchContent(content);
    setActiveTestContent(content); // Unique ID basically
    setResearchModalOpen(true);
  };

  const parseTimestamp = (value) => {
    if (!value) return null;
    const raw = value instanceof Date ? value : new Date(value);
    if (isNaN(raw.getTime())) return null;
    return raw;
  };

  const formatTime = (timestamp) => {
    const date = parseTimestamp(timestamp);
    if (!date) return "Just now";
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const hasMessages = conversation?.messages && conversation.messages.length > 0;
  const brandName =
    brandVisualTheme?.brandName ??
    brandApplicationSlice?.brand ??
    brandProfileSpec?.brand?.name ??
    brandMeta?.name ??
    'BrandOn';
  const brandTagline =
    brandVisualTheme?.tagline ??
    humanizeToken(brandApplicationSlice?.voice?.differentiator) ??
    brandProfileSpec?.brand?.industry ??
    brandMeta?.industry ??
    'Your on-brand AI partner';
  const themeLogo = brandVisualTheme?.logosByMode?.[resolvedTheme] ?? brandVisualTheme?.logo;
  const brandLogo = themeLogo?.uri ?? 'https://customer-assets.emergentagent.com/job_dual-view-agent/artifacts/ls3jjxc1_noderiver_white.svg';
  const brandLogoAlt = themeLogo?.alt ?? `${brandName} logo`;

  return (
    <div
      className="h-screen flex flex-col"
      style={containerStyle}
    >
      {/* Top Bar - Minimal */}
      <div className="absolute top-0 left-0 right-0 px-6 py-4 flex items-center justify-between z-10">
        <button
          onClick={canSendMessage ? onSwitchView : onRequireAuth}
          className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-colors ${
            isBrandTheme 
              ? 'hover:bg-white/10 text-white' 
              : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'
          } ${!canSendMessage ? 'opacity-60' : ''}`}
        >
          <LayoutGrid className="w-4 h-4" />
          <span className="text-sm font-medium">
            {canSendMessage ? 'Split View' : 'Sign in for Split View'}
          </span>
        </button>
        
        {/* Branch controls - only show when there are messages */}
        {hasMessages && (
          <div className="flex items-center gap-2">
            <BranchIndicator
              conversation={conversation}
              branchIndex={branchIndex}
              totalBranches={totalBranches}
              onOpenTree={onOpenBranchTree}
              brandVisualTheme={brandVisualTheme}
              isBrandTheme={isBrandTheme}
            />
            
            {onOpenBranchTree && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onOpenBranchTree}
                      className="h-8 w-8 p-0 rounded-lg transition-colors"
                      style={{
                        color: isBrandTheme ? 'rgba(255,255,255,0.7)' : mutedColor,
                        backgroundColor: 'transparent',
                      }}
                    >
                      <GitBranch className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs px-2 py-1">
                    View branch tree
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>

      {/* Main Content - Google Search Style */}
      {!hasMessages ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-2xl">
            {(canSendMessage || brandOptions.length > 0) && (brandOptions.length > 1 || environmentOptions.length > 0) && (
              <div className="flex flex-wrap gap-3 justify-center mb-6">
                {brandOptions.length > 1 && onBrandChange && (
                  <div className="w-full sm:w-auto">
                    <Select value={String(selectedBrandId ?? '')} onValueChange={onBrandChange}>
                      <SelectTrigger className="min-w-[200px] bg-white/10 border-white/30 text-white">
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 text-white border border-white/10">
                        {brandOptions.map((brand) => (
                          <SelectItem key={brand.id} value={String(brand.id)}>
                            {brand.name ?? brand.slug ?? brand.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {environmentOptions.length > 0 && onEnvironmentChange && (
                  <div className="w-full sm:w-auto">
                    <Select value={selectedEnvironment} onValueChange={onEnvironmentChange}>
                      <SelectTrigger className="min-w-[200px] bg-white/10 border-white/30 text-white">
                        <SelectValue placeholder="Environment" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 text-white border border-white/10">
                        {environmentOptions.map((env) => (
                          <SelectItem key={env.key} value={env.key}>
                            {env.label ?? env.key}
                            {env.is_default ? ' • default' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            {/* Brand Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6 p-6" style={{ background: heroGradient }}>
                <img
                  src={brandLogo}
                  alt={brandLogoAlt}
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-4xl font-semibold mb-3" style={{ color: textColor }}>{brandName}</h1>
              <p className="text-base" style={{ color: mutedColor }}>
                {brandTagline}
              </p>
            </div>

            {/* Search Box Style Input */}
            <div className="mb-6">
              <div className="relative">
                <div
                  className="relative rounded-full transition-all px-5 py-4 border"
                  style={{
                    backgroundColor: isBrandTheme ? 'rgba(255,255,255,0.95)' : inputBg,
                    borderColor: isBrandTheme ? 'rgba(255,255,255,0.35)' : themeMode?.border ?? '#e5e7eb',
                    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = 'inset 0 3px 8px rgba(0, 0, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.06)';
                  }}
                >
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask BrandOn to create on-brand emails, website content, or marketing copy..."
                    disabled={isStreaming}
                    rows={1}
                    className="w-full resize-none text-base bg-transparent pr-14 placeholder:text-gray-500 dark:placeholder:text-gray-400"
                    style={{
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      color: textColor,
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!message.trim() || isStreaming}
                    className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105"
                    style={{
                      background: message.trim() && !isStreaming ? accentGradient : (isBrandTheme ? 'rgba(255,255,255,0.4)' : '#e5e7eb'),
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: message.trim() && !isStreaming ? `0 2px 8px ${applyAlpha(accentColor, 0.3)}` : 'none'
                    }}
                  >
                    <ArrowUp className="w-5 h-5" strokeWidth={3} style={{ color: accentContrast }} />
                  </button>
                </div>
              </div>
            </div>

            {/* Agent Status */}
            <div className="flex items-center justify-center space-x-2">
              {isStreaming ? (
                <>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: statusColors.working }}></div>
                  <span 
                    className="text-sm animate-pulse-subtle animate-gradient-text" 
                    style={{ 
                      backgroundImage: 'linear-gradient(to right, #1E8997, #3b82f6, #8b5cf6, #1E8997)' 
                    }}
                  >
                    {streamingContent ? 'BrandOn is responding...' : 'BrandOn is thinking...'}
                  </span>
                </>
              ) : message.trim() ? (
                <>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors.idle }}></div>
                  <span className="text-sm" style={{ color: mutedColor }}>Agent is waiting...</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors.ready }}></div>
                  <span className="text-sm" style={{ color: mutedColor }}>
                    {canSendMessage ? 'Ready when you are' : 'Sign in to get started'}
                  </span>
                </>
              )}
            </div>

            {!canSendMessage && (
              <p className="text-sm mt-4 text-center" style={{ color: mutedColor }}>
                Sign in from the account menu to start a conversation.
              </p>
            )}

            {/* Quick Actions */}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => setMessage(action.value)}
                  disabled={isStreaming}
                  className="px-4 py-2 rounded-full border hover:shadow-sm transition-all text-sm flex items-center gap-2"
                  style={{
                    borderColor: quickActionBorder,
                    color: quickActionText,
                    backgroundColor: quickActionBackground,
                    backdropFilter: isBrandTheme ? 'blur(6px)' : undefined,
                  }}
                >
                  <action.icon className="w-4 h-4" />
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-start justify-center py-20 px-4 overflow-hidden mt-16">
          <div className="w-full max-w-3xl h-full flex flex-col">
            <ScrollArea className="flex-1 px-4" ref={scrollRef}>
              <div className="space-y-6">
                {conversation.messages.map((msg) => (
                  <React.Fragment key={msg.id}>
                    <div className={`flex items-start space-x-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                      {msg.role === 'assistant' && (
                        <Avatar className="w-8 h-8" style={{ background: accentGradient }}>
                          <AvatarFallback className="bg-transparent"><img src="/noderiver.svg" alt="BrandOn" className="w-4 h-4" /></AvatarFallback>
                        </Avatar>
                      )}
                      <div
                        className="max-w-[70%] rounded-2xl px-4 py-3"
                        style={
                          msg.role === 'user'
                            ? { background: accentGradient, color: accentContrast }
                            : {
                                backgroundColor: isBrandTheme ? 'rgba(255,255,255,0.08)' : themeMode?.surface ?? '#f3f4f6',
                                color: msg.role === 'assistant' && isBrandTheme ? textColor : themeMode?.text ?? '#111827',
                              }
                        }
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                        <span className="text-xs opacity-70 mt-2 block" style={{ color: isBrandTheme ? 'rgba(255,255,255,0.7)' : '#6b7280' }}>
                          {formatTime(msg.created_at ?? msg.createdAt)}
                        </span>
                      </div>
                      {msg.role === 'user' && (
                        <Avatar className="w-8 h-8 bg-gray-800">
                          {sessionUser?.image ? <AvatarImage src={sessionUser.image} /> : null}
                          <AvatarFallback className="bg-transparent">
                            <User className="w-4 h-4 text-white" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                    {/* Message Actions */}
                    <MessageActions
                      message={msg}
                      role={msg.role}
                      onCopy={() => {}}
                      onBranch={onBranchMessage ? (m) => onBranchMessage(m) : undefined}
                      onTestAudience={(m) => handleOpenResearch(m.content)}
                      showTestAudience={msg.role === 'assistant' && !!msg.preview?.type}
                      brandVisualTheme={brandVisualTheme}
                      isStreaming={isStreaming}
                    />
                  </React.Fragment>
                ))}
                {isStreaming && (
                  <div className="flex items-start space-x-3">
                    <Avatar className="w-8 h-8" style={{ background: accentGradient }}>
                      <AvatarFallback className="bg-transparent"><img src="/noderiver.svg" alt="BrandOn" className="w-4 h-4" /></AvatarFallback>
                    </Avatar>
                    <div
                      className="max-w-[70%] rounded-2xl px-4 py-3"
                      style={{
                        backgroundColor: isBrandTheme ? 'rgba(255,255,255,0.08)' : themeMode?.surface ?? '#f3f4f6',
                        color: isBrandTheme ? textColor : themeMode?.text ?? '#111827',
                      }}
                    >
                      {streamingContent ? (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {streamingContent}
                          <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse opacity-70" />
                        </p>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <span 
                            className="text-xs font-medium animate-pulse-subtle animate-gradient-text" 
                            style={{ 
                              backgroundImage: 'linear-gradient(to right, #1E8997, #3b82f6, #8b5cf6, #1E8997)' 
                            }}
                          >
                            BrandOn is thinking...
                          </span>
                          <div className="flex space-x-2">
                            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: statusColors.working, animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: statusColors.working, animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: statusColors.working, animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
      {!!onLogin && (
        <div className="fixed bottom-6 left-6 z-20 max-w-xs">
          <AccountMenu
            collapsed={false}
            authStatus={authStatus}
            user={sessionUser}
            accountLabel={accountLabel}
            onLogin={onLogin}
            onLogout={onLogout}
            brandVisualTheme={brandVisualTheme}
            onOpenSettings={() => setShowSettings(true)}
          />
        </div>
      )}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        brandVisualTheme={brandVisualTheme}
        user={sessionUser}
        onUpdateUser={onUpdateUser}
      />
      <MarketResearchModal
        open={researchModalOpen}
        onClose={() => setResearchModalOpen(false)}
        conversationId={conversation?.id}
        content={researchContent}
        brandVisualTheme={brandVisualTheme}
        // Force remount when content changes to clear internal state (scores)
        key={activeTestContent} 
      />
    </div>
  );
};

export default SimpleChatView;
